import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../api';

const NAV_ITEMS = [
  { to: '/my-games', label: 'Play', icon: '▶︎', primary: true },
  { to: '/editor', label: 'Editor' },
];

export default function Navbar() {
  const ref = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const auth = useAuth();
  const { pathname } = useLocation();

  const handleClickBurger = () => setOpen(!open);
  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const callback = (event: MouseEvent) => {
      const isOutside = !ref.current?.contains(event.target as HTMLElement);
      if (isOutside) close();
    };
    window.addEventListener('mousedown', callback);
    return () => window.removeEventListener('mousedown', callback);
  }, [open]);

  const isActive = (to: string) =>
    pathname === to || pathname.startsWith(to + '/');

  return (
    <nav
      className="navbar has-shadow is-spaced"
      role="navigation"
      aria-label="main navigation"
      ref={ref}
    >
      <div className="navbar-brand">
        <div
          className="navbar-item"
          style={{ fontWeight: 700, letterSpacing: '0.02em' }}
        >
          BannerWars
        </div>
        <a
          role="button"
          className={`navbar-burger ${open ? 'is-active' : ''}`}
          aria-label="menu"
          aria-expanded={open}
          onClick={handleClickBurger}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </a>
      </div>

      <div id="navbarMenu" className={`navbar-menu ${open ? 'is-active' : ''}`}>
        <div className="navbar-start">
          {auth &&
            NAV_ITEMS.map((item) => (
              <div
                key={item.to}
                className="navbar-item nav-link-item"
                data-active={isActive(item.to)}
              >
                <Link
                  className={`button nav-link-btn${
                    item.primary ? ' is-primary' : ' is-light'
                  }`}
                  to={item.to}
                  onClick={close}
                >
                  {item.icon && (
                    <span style={{ fontSize: '0.9rem' }}>{item.icon}</span>
                  )}
                  {item.label}
                </Link>
              </div>
            ))}
        </div>

        <hr
          className="navbar-divider mobile-only"
          style={{ display: 'none' }}
        />

        <div className="navbar-end">
          {auth ? (
            <>
              <Link className="navbar-item" to="/account" onClick={close}>
                {auth.username}
              </Link>
              <Link className="navbar-item" to="/settings" onClick={close}>
                Settings
              </Link>
              <div className="navbar-item">
                <div className="buttons">
                  <Link
                    className="button is-light"
                    to="/logout"
                    onClick={close}
                  >
                    Log out
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <div className="navbar-item">
              <div className="buttons">
                <Link
                  className="button is-primary"
                  to="/register"
                  onClick={close}
                >
                  <strong>Register</strong>
                </Link>
                <Link className="button is-light" to="/login" onClick={close}>
                  Log in
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .navbar {
          padding-top: max(8px, env(safe-area-inset-top));
          flex-shrink: 0;
          position: relative;
          z-index: 30;
        }

        @media screen and (max-width: 1023px) {
          .navbar-menu.is-active {
            box-shadow: 0 4px 16px rgba(0,0,0,0.35);
            border-bottom: 1px solid var(--panel-border, #444466);
            padding-bottom: 12px;
          }

          .navbar-menu.is-active .navbar-item {
            padding: 6px 16px;
          }

          .nav-link-btn {
            width: 100%;
            justify-content: flex-start;
            gap: 8px;
            height: 44px;
            font-size: 0.95rem;
          }

          .mobile-only {
            display: block !important;
            margin: 4px 16px;
            border-color: rgba(68,68,102,0.35);
          }

          .nav-link-item[data-active="true"] {
            border-left: 3px solid var(--primary);
            padding-left: 13px;
          }
        }
      `}</style>
    </nav>
  );
}
